import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from '@mui/material';
import {
  Phone,
  Email,
  LocationOn,
  Schedule,
  DirectionsCar,
  Accessible,
} from '@mui/icons-material';
import { motion } from 'framer-motion';

const MotionCard = motion(Card);
const MotionBox = motion(Box);

const Contact: React.FC = () => {
  const [formData, setFormData] = React.useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const subject = `Contact Form Submission - ${formData.service || 'General Inquiry'}`;
    const body = `Name: ${formData.name}%0D%0AEmail: ${formData.email}%0D%0APhone: ${formData.phone}%0D%0AService: ${formData.service}%0D%0AMessage: ${formData.message}`;
    
    window.location.href = `mailto:info@healthbychiropractic.com.au?subject=${subject}&body=${body}`;
  };

  const contactInfo = [
    {
      icon: <Phone />,
      title: 'Phone',
      details: '(03) 9743 5510',
      action: 'tel:(03) 9743 5510',
    },
    {
      icon: <Phone />,
      title: 'Fax',
      details: '(03) 9743 0138',
      action: 'tel:(03) 9743 0138',
    },
    {
      icon: <Email />,
      title: 'Email',
      details: 'info@healthbychiropractic.com.au',
      action: 'mailto:info@healthbychiropractic.com.au',
    },
    {
      icon: <LocationOn />,
      title: 'Address',
      details: '477 High Street, Melton VIC 3337',
      action: 'https://maps.google.com/?q=477+High+Street,+Melton+VIC+3337',
    },
  ];

  const businessHours = [
    { day: 'Monday', hours: '9:00am - 6:30pm' },
    { day: 'Tuesday', hours: '12:00pm - 7:00pm' },
    { day: 'Wednesday', hours: '9:00am - 6:30pm' },
    { day: 'Thursday', hours: 'Closed' },
    { day: 'Friday', hours: '9:30am - 6:00pm' },
    { day: 'Saturday', hours: '8:00am - 12:00pm' },
    { day: 'Sunday', hours: 'Closed' },
  ];

  const services = [
    'Back Pain',
    'Neck Pain',
    'Sports Injuries',
    'Workplace Injuries',
    'Disc Bulges',
    'Sciatic Pain',
    'Pregnancy Care',
    'Vertigo',
    'Headaches',
    'Myotherapy & Remedial Massage',
  ];

  return (
    <Box
      id="contact"
      sx={{
        py: { xs: 6, md: 10 },
        backgroundColor: '#f8f9fa',
      }}
    >
      <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
        <MotionBox
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}
        >
          <Typography
            variant="h2"
            sx={{
              mb: { xs: 2, md: 2 },
              background: 'linear-gradient(45deg, #2c3e50, #8bc34a)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontSize: { xs: '1.8rem', sm: '2.2rem', md: '3rem' },
              px: { xs: 2, md: 0 },
            }}
          >
            Book Your Appointment
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: 'text.secondary',
              maxWidth: '600px',
              mx: 'auto',
              fontSize: { xs: '0.95rem', sm: '1.1rem', md: '1.25rem' },
              px: { xs: 3, md: 0 },
              lineHeight: { xs: 1.6, md: 1.6 },
            }}
          >
            Take the first step towards better health. Contact us today to schedule
            your consultation with our expert team.
          </Typography>
        </MotionBox>

        <Grid container spacing={{ xs: 4, md: 6 }}>
          <Grid item xs={12} lg={6}>
            <MotionCard
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              sx={{
                borderRadius: { xs: '12px', md: '16px' },
                border: 'none',
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                height: 'fit-content',
                mx: { xs: 1, lg: 0 },
              }}
            >
              <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    mb: { xs: 2.5, md: 3 }, 
                    fontWeight: 600,
                    fontSize: { xs: '1.3rem', md: '1.5rem' },
                  }}
                >
                  Send us a Message
                </Typography>

                <Box component="form" onSubmit={handleSubmit}>
                  <Grid container spacing={{ xs: 2.5, md: 3 }}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Full Name"
                        variant="outlined"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        sx={{
                          '& .MuiInputBase-root': {
                            fontSize: { xs: '0.875rem', md: '1rem' },
                          },
                          '& .MuiInputLabel-root': {
                            fontSize: { xs: '0.875rem', md: '1rem' },
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Email"
                        type="email"
                        variant="outlined"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        sx={{
                          '& .MuiInputBase-root': {
                            fontSize: { xs: '0.875rem', md: '1rem' },
                          },
                          '& .MuiInputLabel-root': {
                            fontSize: { xs: '0.875rem', md: '1rem' },
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Phone Number"
                        variant="outlined"
                        required
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        sx={{
                          '& .MuiInputBase-root': {
                            fontSize: { xs: '0.875rem', md: '1rem' },
                          },
                          '& .MuiInputLabel-root': {
                            fontSize: { xs: '0.875rem', md: '1rem' },
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>
                          Service Needed
                        </InputLabel>
                        <Select
                          value={formData.service}
                          label="Service Needed"
                          onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                          sx={{
                            '& .MuiSelect-select': {
                              fontSize: { xs: '0.875rem', md: '1rem' },
                            },
                          }}
                        >
                          {services.map((service) => (
                            <MenuItem key={service} value={service}>
                              {service}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Message (Optional)"
                        multiline
                        rows={4}
                        variant="outlined"
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        placeholder="Tell us about your symptoms or any specific concerns..."
                        sx={{
                          '& .MuiInputBase-root': {
                            fontSize: { xs: '0.875rem', md: '1rem' },
                          },
                          '& .MuiInputLabel-root': {
                            fontSize: { xs: '0.875rem', md: '1rem' },
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        fullWidth
                        sx={{
                          background: 'linear-gradient(45deg, #8bc34a, #689f38)',
                          py: { xs: 1.3, md: 1.5 },
                          fontSize: { xs: '1rem', md: '1.1rem' },
                          '&:hover': {
                            background: 'linear-gradient(45deg, #689f38, #558b2f)',
                            transform: 'scale(1.02)',
                          },
                          boxShadow: '0 4px 15px rgba(139, 195, 74, 0.3)',
                        }}
                      >
                        Book Appointment Online
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              </CardContent>
            </MotionCard>
          </Grid>

          <Grid item xs={12} lg={6}>
            <Grid container spacing={{ xs: 2.5, md: 3 }}>
              {contactInfo.map((info, index) => (
                <Grid item xs={12} key={index}>
                  <MotionCard
                    initial={{ opacity: 0, x: 50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    sx={{
                      borderRadius: { xs: '10px', md: '12px' },
                      border: 'none',
                      boxShadow: '0 2px 15px rgba(0,0,0,0.08)',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      mx: { xs: 1, lg: 0 },
                      '&:hover': {
                        transform: { xs: 'none', md: 'translateY(-2px)' },
                        boxShadow: { xs: '0 2px 15px rgba(0,0,0,0.08)', md: '0 4px 20px rgba(0,0,0,0.12)' },
                      },
                    }}
                    onClick={() => window.open(info.action)}
                  >
                    <CardContent sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      p: { xs: 2.5, md: 3 } 
                    }}>
                      <Box
                        sx={{
                          width: { xs: 40, md: 50 },
                          height: { xs: 40, md: 50 },
                          borderRadius: { xs: '8px', md: '10px' },
                          backgroundColor: '#e8f5e8',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'primary.main',
                          mr: { xs: 1.5, md: 2 },
                          flexShrink: 0,
                          '& svg': { fontSize: { xs: 20, md: 24 } },
                        }}
                      >
                        {info.icon}
                      </Box>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            fontWeight: 600, 
                            mb: 0.5,
                            fontSize: { xs: '1rem', md: '1.25rem' },
                          }}
                        >
                          {info.title}
                        </Typography>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: 'text.secondary',
                            fontSize: { xs: '0.8rem', md: '0.875rem' },
                            lineHeight: 1.4,
                            wordBreak: 'break-word',
                          }}
                        >
                          {info.details}
                        </Typography>
                      </Box>
                    </CardContent>
                  </MotionCard>
                </Grid>
              ))}

              <Grid item xs={12}>
                <MotionCard
                  initial={{ opacity: 0, x: 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                  viewport={{ once: true }}
                  sx={{
                    borderRadius: { xs: '10px', md: '12px' },
                    border: 'none',
                    boxShadow: '0 2px 15px rgba(0,0,0,0.08)',
                    mx: { xs: 1, lg: 0 },
                  }}
                >
                  <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 1.5, md: 2 } }}>
                      <Schedule sx={{ 
                        color: 'primary.main', 
                        mr: 1,
                        fontSize: { xs: 20, md: 24 },
                      }} />
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          fontWeight: 600,
                          fontSize: { xs: '1rem', md: '1.25rem' },
                        }}
                      >
                        Business Hours
                      </Typography>
                    </Box>
                    {businessHours.map((schedule, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          py: { xs: 0.4, md: 0.5 },
                          borderBottom: index < businessHours.length - 1 ? '1px solid #f0f0f0' : 'none',
                          gap: 2,
                        }}
                      >
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontWeight: 500,
                            fontSize: { xs: '0.8rem', md: '0.875rem' },
                            flexShrink: 0,
                          }}
                        >
                          {schedule.day}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: schedule.hours === 'Closed' ? 'text.disabled' : 'text.secondary',
                            fontSize: { xs: '0.75rem', md: '0.875rem' },
                            textAlign: 'right',
                            lineHeight: 1.3,
                          }}
                        >
                          {schedule.hours}
                        </Typography>
                      </Box>
                    ))}
                  </CardContent>
                </MotionCard>
              </Grid>

              <Grid item xs={12}>
                <MotionBox
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.6 }}
                  viewport={{ once: true }}
                  sx={{ px: { xs: 1, lg: 0 } }}
                >
                  <Box sx={{ 
                    display: 'flex', 
                    gap: { xs: 0.5, md: 1 }, 
                    flexWrap: 'wrap',
                    justifyContent: { xs: 'center', lg: 'flex-start' },
                  }}>
                    <Chip
                      icon={<DirectionsCar sx={{ fontSize: { xs: 16, md: 20 } }} />}
                      label="Free Parking Available"
                      sx={{ 
                        backgroundColor: '#e8f5e8', 
                        color: '#2e7d32',
                        fontSize: { xs: '0.7rem', md: '0.75rem' },
                        height: { xs: '28px', md: '32px' },
                      }}
                    />
                    <Chip
                      icon={<Accessible sx={{ fontSize: { xs: 16, md: 20 } }} />}
                      label="Wheelchair Accessible"
                      sx={{ 
                        backgroundColor: '#e8f5e8', 
                        color: '#2c3e50',
                        fontSize: { xs: '0.7rem', md: '0.75rem' },
                        height: { xs: '28px', md: '32px' },
                        fontWeight: 500,
                      }}
                    />
                  </Box>
                </MotionBox>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <MotionBox
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          viewport={{ once: true }}
          sx={{ 
            mt: { xs: 6, md: 8 }, 
            textAlign: 'center',
            px: { xs: 2, md: 0 },
          }}
        >
          <Typography 
            variant="h6" 
            sx={{ 
              mb: { xs: 1.5, md: 2 }, 
              fontWeight: 600,
              fontSize: { xs: '1.1rem', md: '1.25rem' },
            }}
          >
            Prefer to Call?
          </Typography>
          <Button
            variant="outlined"
            size="large"
            startIcon={<Phone sx={{ fontSize: { xs: 18, md: 20 } }} />}
            href="tel:(03) 9743 5510"
            sx={{
              px: { xs: 3, md: 4 },
              py: { xs: 1.2, md: 1.5 },
              fontSize: { xs: '1rem', md: '1.1rem' },
              borderColor: '#8bc34a',
              color: '#8bc34a',
              minWidth: { xs: '250px', md: 'auto' },
              borderWidth: 2,
              fontWeight: 600,
              '&:hover': {
                backgroundColor: '#8bc34a',
                borderColor: '#8bc34a',
                color: 'white',
              },
            }}
          >
            Call (03) 9743 5510 Now
          </Button>
        </MotionBox>
      </Container>
    </Box>
  );
};

export default Contact;